import { supabase } from "./supabase";
import type { Listing, ListingAnalysis, ListingOffer, OfferProjectionYear, ScenarioTemplate, ScenarioPartner, ScenarioPartnerProjectionYear, GlobalDefault, RegionState, VehicleCohort, ListingVehicle } from "./types";

// Row shape for the listings/offers overview page: ONE ROW PER OFFER
// (not per listing) -- a listing with multiple offers (Default +
// custom) appears multiple times, once per offer, each independently
// sortable/filterable since they'll have different numbers. Listing-
// level fields (source, routes, asking_price, state, etc.) are
// duplicated onto each offer row for display/filtering purposes.
export interface OfferRow extends Listing {
  listing_analysis: Pick<ListingAnalysis, "deal_type" | "multiplier" | "total_price"> | null;
  listing_offers: Pick<
    ListingOffer,
    "id" | "name" | "projected_take_home" | "multiplier" | "total_cash" | "purchase_price" | "seller_financing_standby" | "starred" | "cash_structure_seller_financing_rate"
  >[];
}

export async function fetchListings(): Promise<OfferRow[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(
      `*,
       listing_analysis (deal_type, multiplier, total_price),
       listing_offers (id, name, projected_take_home, multiplier, total_cash, purchase_price, seller_financing_standby, starred, cash_structure_seller_financing_rate)`
    )
    .eq("record_status", "Active")
    .order("last_seen", { ascending: false });

  if (error) throw error;
  return (data ?? []) as unknown as OfferRow[];
}

export async function toggleOfferStar(offerId: string, starred: boolean): Promise<void> {
  const { error } = await supabase.from("listing_offers").update({ starred }).eq("id", offerId);
  if (error) throw error;
}

export async function updateListingNotes(listingUid: string, notes: string): Promise<void> {
  const { error } = await supabase.from("listings").update({ notes }).eq("uid", listingUid);
  if (error) throw error;
}

export interface CreateManualListingInput {
  title: string;
  listing_url?: string;
  route_type?: string;
  location?: string;
  asking_price?: number;
  revenue?: number;
  cash_flow_noi?: number;
  routes_count?: number;
  fleet_value?: number;
  total_employees?: number;
  trucks?: number;
  description?: string;
}

// For deals found outside the Route Consultant / FXG scrapers. Goes
// through the API (not a direct Supabase insert) since server-side
// logic assigns the MANUAL: uid prefix and Active/first_seen/last_seen
// defaults -- see api.py's create_manual_listing.
export async function createManualListing(input: CreateManualListingInput): Promise<Listing> {
  const resp = await fetch("/api/listings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Failed to create listing (${resp.status})`);
  }
  return data;
}

export async function fetchListing(uid: string): Promise<Listing | null> {
  const { data, error } = await supabase
    .from("listings")
    .select("*")
    .eq("uid", uid)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchListingAnalysis(uid: string): Promise<ListingAnalysis | null> {
  const { data, error } = await supabase
    .from("listing_analysis")
    .select("*")
    .eq("listing_uid", uid)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchOffersForListing(uid: string): Promise<ListingOffer[]> {
  const { data, error } = await supabase
    .from("listing_offers")
    .select("*")
    .eq("listing_uid", uid)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function fetchBusinessProjection(offerId: string): Promise<OfferProjectionYear[]> {
  const { data, error } = await supabase
    .from("offer_projections")
    .select("*")
    .eq("offer_id", offerId)
    .order("year", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function fetchVehicleCohorts(offerId: string): Promise<VehicleCohort[]> {
  const { data, error } = await supabase
    .from("vehicle_cohorts")
    .select("*")
    .eq("offer_id", offerId)
    .order("in_service_year", { ascending: true })
    .order("vehicle_id", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

// Fetches every scenario (100% Ownership, 80/20 Split, ...) this offer
// has projections for, resolving scenario/partner display names so
// components don't need to separately join scenario_templates/
// scenario_partners themselves.
export async function fetchPartnerProjections(offerId: string): Promise<{
  scenarios: ScenarioTemplate[];
  partners: ScenarioPartner[];
  years: ScenarioPartnerProjectionYear[];
}> {
  const { data: years, error: yearsError } = await supabase
    .from("scenario_partner_projections")
    .select("*")
    .eq("offer_id", offerId)
    .order("year", { ascending: true });
  if (yearsError) throw yearsError;

  const templateIds = [...new Set((years ?? []).map((y) => y.scenario_template_id))];
  const partnerIds = [...new Set((years ?? []).map((y) => y.scenario_partner_id))];

  const [{ data: scenarios, error: scenariosError }, { data: partners, error: partnersError }] =
    await Promise.all([
      templateIds.length
        ? supabase.from("scenario_templates").select("*").in("id", templateIds)
        : Promise.resolve({ data: [], error: null }),
      partnerIds.length
        ? supabase.from("scenario_partners").select("*").in("id", partnerIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
  if (scenariosError) throw scenariosError;
  if (partnersError) throw partnersError;

  // Display order: library templates first (by sort_order, edited via
  // Settings > Templates drag-and-drop), forks always after, oldest
  // first (created_at) -- per user decision. Sorted client-side since
  // this is a .in() query, not a simple ordered select.
  const sortedScenarios = [...(scenarios ?? [])].sort((a, b) => {
    if (a.is_library_template !== b.is_library_template) {
      return a.is_library_template ? -1 : 1;
    }
    if (a.is_library_template) {
      return (a.sort_order ?? 999999) - (b.sort_order ?? 999999);
    }
    return a.created_at.localeCompare(b.created_at);
  });

  return {
    scenarios: sortedScenarios,
    partners: partners ?? [],
    years: years ?? [],
  };
}

export async function fetchGlobalDefaults(): Promise<GlobalDefault[]> {
  const { data, error } = await supabase
    .from("global_defaults")
    .select("*")
    .order("key", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function updateGlobalDefault(key: string, numeric_value: number): Promise<void> {
  const { error } = await supabase
    .from("global_defaults")
    .update({ numeric_value, updated_at: new Date().toISOString() })
    .eq("key", key);

  if (error) throw error;
}

// Region states: the configurable list behind the listings page's
// "region" quick-select button on the state filter. Fetched fresh
// each time Settings loads (not cached) since it's edited rarely and
// staleness would be confusing.
export async function fetchRegionStates(): Promise<RegionState[]> {
  const { data, error } = await supabase.from("region_states").select("*").order("state", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function updateRegionStates(states: RegionState[]): Promise<void> {
  // One upsert covering every row -- simpler and fewer round-trips
  // than diffing which states changed, and the table is only 50 rows.
  const { error } = await supabase.from("region_states").upsert(states, { onConflict: "state" });
  if (error) throw error;
}

// Creates a new offer by cloning an existing one's values, with the
// caller's edits applied on top. Deal-term fields are IMMUTABLE once
// an offer exists (per user decision) -- the only way to "change" them
// is to create a new offer, never update in place. multiplier/
// total_cash/total_project_cost/sba_loan_amount are generated columns
// (or app-computed, in multiplier's case) and are recomputed here
// rather than copied, since they depend on fields that may have
// changed.
// Creates a new offer by cloning an existing one's values, with the
// caller's edits applied on top. Deal-term fields are IMMUTABLE once
// an offer exists (per user decision) -- the only way to "change" them
// is to create a new offer, never update in place.
//
// The percent-to-dollar conversion for down_payment/
// seller_financing_standby happens SERVER-SIDE (POST /offers, proxied
// via /api/offers) -- this function sends percentages through, not
// dollars, so there is exactly one place that formula exists. (A
// frontend-side duplicate of this conversion previously computed
// down_payment off purchase_price instead of total_project_cost --
// fixed by removing the duplicate entirely, not just correcting it.)
export async function createOfferFromClone(
  sourceOffer: ListingOffer,
  edits: {
    deal_type?: "sba_structure" | "cash_structure";
    purchase_price?: number;
    down_payment_pct?: number;
    seller_financing_standby_pct?: number;
    seller_financing_standby_rate_pct?: number;
    sba_loan_rate_pct?: number;
    sba_loan_term_years?: number;
    total_project_cost_multiplier?: number;
    outright_cash?: number;
    seller_financing?: number;
    vehicle_financing?: number;
    vehicle_financing_down_pct?: number;
    // Nullable on purpose: null means "inherit the global default"
    // and MUST be distinguishable from "not edited". See the resolve
    // below.
    federal_depreciation_use_macrs?: number | null;
    cash_structure_seller_financing_rate_pct?: number;
    cash_structure_seller_financing_term_years?: number;
    cash_structure_seller_financing_balloon_year?: number | null;
    fleet_annual_cost_per_vehicle?: number;
    noi_annual_growth_pct_input?: number;
    owner_operator_salary?: number;
  },
  name: string,
  cashFlowNoi: number | null
): Promise<ListingOffer> {
  const body = {
    listing_uid: sourceOffer.listing_uid,
    name,
    // deal_type is now an EDITABLE override, not always inherited from
    // the source offer -- lets a custom offer be flipped to the other
    // structure (sba_structure <-> cash_structure) independent of the
    // listing's Default Offer type, per user request. Falls back to
    // the source offer's own type when not explicitly overridden
    // (normal edit-without-toggling case).
    deal_type: edits.deal_type ?? sourceOffer.deal_type,
    purchase_price: edits.purchase_price ?? sourceOffer.purchase_price ?? 0,
    down_payment_pct: edits.down_payment_pct,
    seller_financing_standby_pct: edits.seller_financing_standby_pct,
    seller_financing_standby_rate_pct: edits.seller_financing_standby_rate_pct,
    sba_loan_rate_pct: edits.sba_loan_rate_pct,
    sba_loan_term_years: edits.sba_loan_term_years,
    total_project_cost_multiplier: edits.total_project_cost_multiplier ?? sourceOffer.total_project_cost_multiplier,
    outright_cash: edits.outright_cash,
    seller_financing: edits.seller_financing,
    // vehicle_financing is now editable too (was previously always
    // carried forward from sourceOffer, which broke on an sba->cash
    // toggle since sba_structure offers never populate this field --
    // the toggle backfills a real default from the listing's own
    // analysis row, see OfferDetailsPanel's handleDealTypeToggle).
    vehicle_financing: edits.vehicle_financing ?? sourceOffer.vehicle_financing,
    cash_structure_seller_financing_rate_pct: edits.cash_structure_seller_financing_rate_pct,
    cash_structure_seller_financing_term_years: edits.cash_structure_seller_financing_term_years,
    cash_structure_seller_financing_balloon_year: edits.cash_structure_seller_financing_balloon_year ?? null,
    // Snapshot fields carried forward from the source offer, unless
    // this specific edit changed one of them.
    fleet_annual_cost_per_vehicle: edits.fleet_annual_cost_per_vehicle ?? sourceOffer.fleet_annual_cost_per_vehicle,
    fleet_size_multiplier: sourceOffer.fleet_size_multiplier,
    fleet_capex_start_year: sourceOffer.fleet_capex_start_year,
    effective_fleet_value: sourceOffer.effective_fleet_value,
    fleet_value_source: sourceOffer.fleet_value_source,
    contract_value_pct_of_remainder: sourceOffer.contract_value_pct_of_remainder,
    contract_depreciation_years: sourceOffer.contract_depreciation_years,
    vehicle_financing_down_pct: edits.vehicle_financing_down_pct ?? sourceOffer.vehicle_financing_down_pct,
    // Resolved on KEY PRESENCE, not `??`. null is a meaningful value
    // here (inherit the global default), and `??` treated it as "no
    // edit" and silently restored the source offer's value -- so once
    // an offer was set to MACRS, the Default button could never clear
    // it. Same nullish-coalescing trap as vehicle_financing above.
    federal_depreciation_use_macrs:
      "federal_depreciation_use_macrs" in edits
        ? edits.federal_depreciation_use_macrs
        : sourceOffer.federal_depreciation_use_macrs,
    noi_annual_growth_pct_input: edits.noi_annual_growth_pct_input,
    owner_operator_salary: edits.owner_operator_salary ?? sourceOffer.owner_operator_salary,
    cash_flow_noi: cashFlowNoi,
  };

  const resp = await fetch("/api/offers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Offer creation failed (${resp.status})`);
  }
  return data as ListingOffer;
}

// Deletes an offer. NEVER call this for a Default Offer -- callers
// Notes are metadata, not a deal term -- updates in place directly,
// unlike deal-term fields which always clone+delete (per user
// decision, that rule is specifically about terms that affect the
// projection, not free-text notes).
export async function updateOfferNotes(offerId: string, notes: string): Promise<void> {
  const { error } = await supabase.from("listing_offers").update({ notes }).eq("id", offerId);
  if (error) throw error;
}

// must guard against that (see OfferDetailsPanel: the Edit/Save flow
// that triggers this is only rendered for non-Default offers).
export async function deleteOffer(offerId: string): Promise<void> {
  // Four tables reference offer_id (directly or transitively) and
  // block deleting the parent listing_offers row while they still
  // reference it (409/error=23503, confirmed via real testing).
  // scenario_templates forks (#20: per-offer customization, offer_id
  // set on a fork vs null for library templates) were missed when
  // this function was first written -- caught in production via
  // "violates foreign key constraint scenario_templates_offer_id_fkey"
  // on any offer that had ever forked a template. scenario_partners
  // FKs to scenario_templates.id, so it must go before its parent.
  // Delete children first, deepest first, then the offer.
  const { data: forkedTemplates, error: templateFetchError } = await supabase
    .from("scenario_templates")
    .select("id")
    .eq("offer_id", offerId);
  if (templateFetchError) throw templateFetchError;

  if (forkedTemplates && forkedTemplates.length > 0) {
    const templateIds = forkedTemplates.map((t) => t.id);
    // scenario_partner_projections FKs to scenario_partners.id, so it
    // must go before scenario_partners, not just before listing_offers.
    const { error: partnerProjError } = await supabase
      .from("scenario_partner_projections")
      .delete()
      .in("scenario_template_id", templateIds);
    if (partnerProjError) throw partnerProjError;

    const { error: partnerError } = await supabase.from("scenario_partners").delete().in("scenario_template_id", templateIds);
    if (partnerError) throw partnerError;

    const { error: templateError } = await supabase.from("scenario_templates").delete().in("id", templateIds);
    if (templateError) throw templateError;
  }

  const { error: projError } = await supabase.from("offer_projections").delete().eq("offer_id", offerId);
  if (projError) throw projError;

  const { error: partnerProjError } = await supabase.from("scenario_partner_projections").delete().eq("offer_id", offerId);
  if (partnerProjError) throw partnerProjError;

  const { error } = await supabase.from("listing_offers").delete().eq("id", offerId);
  if (error) throw error;
}

// ---------------------------------------------------------------------
// Scenario templates (#20: custom partner counts/splits)
// ---------------------------------------------------------------------

export interface PartnerSpecInput {
  partner_label: string;
  ownership_pct: number; // WHOLE PERCENT (50 means 50%), matches the API's convention
}

export interface ScenarioTemplateWithPartners extends ScenarioTemplate {
  partners: { id: string; partner_label: string; ownership_pct: number }[];
}

// Fetches every LIBRARY template (for the reusable-template picker) --
// goes through the Next.js proxy to the Python API, not direct
// Supabase, since the API does partner-percentage validation the
// frontend shouldn't duplicate.
export async function fetchLibraryTemplates(): Promise<ScenarioTemplateWithPartners[]> {
  const resp = await fetch("/api/scenario-templates");
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Failed to load templates (${resp.status})`);
  }
  return data;
}

// Persists a new display order for library templates after a
// drag-and-drop reorder on Settings > Templates -- sends the FULL
// ordered list of ids, matching the API's index-based sort_order
// write (see PUT /scenario-templates/order).
export async function reorderLibraryTemplates(templateIds: string[]): Promise<void> {
  const resp = await fetch("/api/scenario-templates/order", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template_ids: templateIds }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || data.error || `Failed to save order (${resp.status})`);
  }
}

export async function createLibraryTemplate(
  name: string,
  partners: PartnerSpecInput[]
): Promise<ScenarioTemplateWithPartners> {
  const resp = await fetch("/api/scenario-templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, partners }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Failed to create template (${resp.status})`);
  }
  return data;
}

// Forks a template for ONE specific offer -- creates a new,
// offer-specific scenario (never mutates the source template), per
// user decision (same clone-then-fork principle as offer editing).
export async function forkScenarioForOffer(
  offerId: string,
  name: string,
  partners: PartnerSpecInput[]
): Promise<ScenarioTemplateWithPartners> {
  const resp = await fetch(`/api/offers/${offerId}/scenarios`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, partners }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Failed to create scenario (${resp.status})`);
  }
  return data;
}

// Deletes a LIBRARY template -- removes this scenario from every offer
// it applied to. Only reachable from the Scenario Templates page (per
// user decision), enforced server-side too (422 if template_id is
// actually an offer-specific fork).
export async function deleteLibraryTemplate(templateId: string): Promise<void> {
  const resp = await fetch(`/api/scenario-templates/${templateId}`, { method: "DELETE" });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || data.error || `Failed to delete template (${resp.status})`);
  }
}

// Deletes an OFFER-SPECIFIC FORK -- removes this scenario from just
// this one offer, library untouched. Only reachable from the offer
// page (per user decision), enforced server-side too (422 if
// template_id is actually a library template).
export async function deleteScenarioFork(offerId: string, templateId: string): Promise<void> {
  const resp = await fetch(`/api/offers/${offerId}/scenarios/${templateId}`, { method: "DELETE" });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || data.error || `Failed to delete scenario (${resp.status})`);
  }
}

// Triggers a GitHub Actions workflow. Goes through /api/workflows/... so
// the GitHub token stays server-side -- see that route for details.
//   "full"   -> full pipeline including both scrapes
//   "reseed" -> analysis + default offers + projections only, no scrape
export async function dispatchWorkflow(
  workflow: "full" | "reseed"
): Promise<{ status: string; workflow: string; actionsUrl: string }> {
  const resp = await fetch(`/api/workflows/${workflow}`, { method: "POST" });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.error || data.detail || `Failed to trigger workflow (${resp.status})`);
  }
  return data;
}

// ---- Fleet list (listing_vehicles) ----

export async function fetchListingVehicles(listingUid: string): Promise<ListingVehicle[]> {
  // Ordered by id: the projection engine assigns ages positionally in
  // this same order, so the UI shows units in the order they're used.
  const { data, error } = await supabase
    .from("listing_vehicles")
    .select("*")
    .eq("listing_uid", listingUid)
    .order("id", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ListingVehicle[];
}

// Replaces a listing's whole vehicle list in one transaction (the
// replace_listing_vehicles RPC). Not delete-then-insert from the
// browser: a failure between those two calls would leave the listing
// with no list, or two copies of it -- and a doubled list doubles the
// projected fleet. Pass [] to clear.
export async function replaceListingVehicles(listingUid: string, vehicles: ListingVehicle[]): Promise<number> {
  const rows = vehicles.map((v) => ({
    unit_number: v.unit_number,
    vin: v.vin,
    model_year: v.model_year,
    make: v.make,
    model: v.model,
    mileage: v.mileage,
    fuel_type: v.fuel_type,
    stated_value: v.stated_value,
    data_source: v.data_source ?? "manual",
  }));
  const { data, error } = await supabase.rpc("replace_listing_vehicles", {
    p_listing_uid: listingUid,
    p_rows: rows,
  });
  if (error) throw error;
  return (data as number) ?? rows.length;
}

export async function updateListingAnnualMiles(listingUid: string, miles: number | null): Promise<void> {
  const { error } = await supabase.from("listings").update({ fleet_annual_miles: miles }).eq("uid", listingUid);
  if (error) throw error;
}
